import { Button, CustomSelect } from '@/components/ui';
import FullPageSpinner from '@/components/ui/FullPageSpinner';
import { useApplicationStatuses, useUpdateApplicationStatus } from '@/lib/api/hooks/application';
import { UpdateApplicationStatus } from '@/lib/api/types';
import React, { useEffect, useState } from 'react';

interface UpdateApplicationStatusModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  selectedApplication?: string | null;
}

export const UpdateApplicationStatusModal: React.FC<UpdateApplicationStatusModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  selectedApplication
}) => {
  const [isAnimating, setIsAnimating] = useState(false);
const [selectedStatus, setSelectedStatus] = useState<string>("");
const updateApplicationStatusMutation = useUpdateApplicationStatus();

  const {data, isFetching, isError, refetch} = useApplicationStatuses();

  const applicationStatusesOptions = Array.isArray(data?.data) ? data.data.map(status => ({ value: status.value, label: status.displayName })) : [];


  useEffect(() => {
    if (isOpen) {
      // Trigger animation after mount
      setTimeout(() => setIsAnimating(true), 10);
    } else {
      setIsAnimating(false);
    }
  }, [isOpen]);

  const handleUpdateStatus = () => {
    if (selectedStatus && selectedApplication) {
      updateApplicationStatusMutation.mutate(
        { applicationId: selectedApplication, data: { status: selectedStatus as UpdateApplicationStatus['status'] } },
        {
          onSuccess: () => {
            onSuccess();
            onClose();
          }
        }
      );
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center  transition-opacity duration-300 ${
        isAnimating ? 'opacity-100' : 'opacity-0'
      }`}
      onClick={onClose}
    >
      <div 
        className={`bg-white rounded-xl shadow-xl max-w-md w-full mx-4 overflow-hidden transition-all duration-300 ease-out ${
          isAnimating ? 'opacity-100 scale-100 translate-y-0' : 'opacity-0 scale-95 translate-y-4'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Illustration Header */}
        <div className="bg-gray-100 p-6 flex items-center justify-center">
          <img 
            src="/post-icon.png" 
            alt="Publish" 
            className="w-full h-auto max-h-48 object-contain" 
          />
        </div>

        {/* Content Area */}
        <div className="p-6 flex flex-col gap-8">
          <h3 className="text-xl font-bold text-gray-900">Update Application Status</h3>

          {
            isFetching ? <FullPageSpinner /> : isError ? <div className="flex flex-col items-center gap-4">
               <p className="">Error fetching application statuses. Please try again.</p> 
              <Button
                onClick={() => refetch()}
                className=""
              >
                Retry
              </Button>
            </div>
               
               : <CustomSelect 
               label="Select New Status"
            options={applicationStatusesOptions}
            placeholder="Select a status"
            value={selectedStatus}
            onChange={(option) => setSelectedStatus(option as string)}
            disabled={isFetching || isError}
          />
          }
          

          {/* Action Buttons */}
          <div className="grid grid-cols-2 gap-4 mt-4">
            <Button
              onClick={onClose}
              disabled={isFetching}
              className="text-white bg-gray-600 rounded-lg hover:bg-gray-700 disabled:opacity-50 transition-colors"
            >
              Cancel
            </Button>
            <Button
              onClick={()=>handleUpdateStatus()}
              disabled={!selectedStatus}
              className="text-white bg-[#047857] rounded-lg hover:bg-[#059669] disabled:opacity-50 transition-colors"
            >
              Yes, Update Status
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UpdateApplicationStatusModal;

